/**
 * modalManager.js — модальные окна: ловушка фокуса, Esc, клик по подложке.
 *
 * [10.0.3] Взято из присланного файла. Что изменено и почему.
 *
 * 1. Слушатели ставятся ОДИН раз на окно, а не при каждом открытии.
 *    В присланном виде open() вешал три обработчика (Tab, Esc, клик по
 *    подложке) и не снимал их никогда. Десять открытий одного и того же
 *    окна — десять комплектов. На Esc окно пыталось закрыться десять раз
 *    подряд, а каждый Tab прогонял ловушку фокуса по кругу столько же раз.
 *    Заметно это становится не сразу, чем и неприятно.
 *
 * 2. Список фокусируемых элементов пересчитывается на каждое нажатие Tab.
 *    Было: снимок при открытии. Содержимое модальных окон здесь строится
 *    на лету (список серий, пресеты, отчёты), и снимок устаревал к первому
 *    же нажатию — фокус уходил на кнопку, которой уже нет.
 *
 * 3. Скрытые элементы из ловушки исключены. Кнопка с display:none попадала
 *    в список и «съедала» фокус: человек жал Tab и не понимал, куда он делся.
 *
 * 4. Esc закрывает ВЕРХНЕЕ окно, а не то, на котором висит обработчик.
 *    Открытых окон может быть несколько (отчёт поверх настроек).
 */

/** Что вообще может принять фокус. */
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), '
  + 'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function _visibleFocusable(root) {
  return [...root.querySelectorAll(FOCUSABLE)].filter((el) => {
    if (el.hasAttribute('inert')) return false;
    // offsetParent === null у скрытых; для position:fixed проверяем размеры.
    const r = el.getBoundingClientRect();
    return (r.width > 0 && r.height > 0);
  });
}

export class ModalManager {
  /** @type {string[]} порядок открытия: последний — верхний */
  static #stack = [];
  /** @type {Map<string, {onKey: Function, onClick: Function}>} */
  static #bound = new Map();
  static #previousFocus = null;

  static open(modalId) {
    const modal = document.getElementById(modalId);
    if (!modal || this.#stack.includes(modalId)) return;

    if (!this.#stack.length) this.#previousFocus = document.activeElement;

    this.#bindOnce(modal);
    modal.classList.add('open');
    modal.setAttribute('aria-hidden', 'false');
    if (!modal.hasAttribute('role')) modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    this.#stack.push(modalId);

    _visibleFocusable(modal)[0]?.focus();
  }

  static close(modalId) {
    const modal = document.getElementById(modalId);
    const i = this.#stack.indexOf(modalId);
    if (!modal || i < 0) return;

    modal.classList.remove('open');
    modal.setAttribute('aria-hidden', 'true');
    modal.removeAttribute('aria-modal');
    this.#stack.splice(i, 1);

    if (!this.#stack.length) {
      const prev = this.#previousFocus;
      this.#previousFocus = null;
      if (prev && typeof prev.focus === 'function' && prev.isConnected) prev.focus();
    } else {
      // Вернуть фокус в окно, которое стало верхним.
      const top = document.getElementById(this.#stack[this.#stack.length - 1]);
      if (top) _visibleFocusable(top)[0]?.focus();
    }
  }

  static closeAll() {
    for (const id of [...this.#stack].reverse()) this.close(id);
  }

  static isOpen(modalId) { return this.#stack.includes(modalId); }
  static topMost() { return this.#stack[this.#stack.length - 1] || null; }

  /** Обработчики вешаются один раз за жизнь окна. */
  static #bindOnce(modal) {
    if (this.#bound.has(modal.id)) return;

    const onKey = (e) => {
      if (!this.#stack.includes(modal.id)) return;
      // Реагирует только верхнее окно: иначе Esc закроет сразу все.
      if (this.topMost() !== modal.id) return;

      if (e.key === 'Escape') { e.stopPropagation(); this.close(modal.id); return; }
      if (e.key !== 'Tab') return;

      // Список считаем сейчас: содержимое окна могло смениться.
      const items = _visibleFocusable(modal);
      if (!items.length) return;
      const first = items[0], last = items[items.length - 1];
      const active = document.activeElement;

      if (e.shiftKey && (active === first || !modal.contains(active))) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && (active === last || !modal.contains(active))) {
        e.preventDefault(); first.focus();
      }
    };

    const onClick = (e) => {
      if (e.target === modal && this.#stack.includes(modal.id)) this.close(modal.id);
    };

    modal.addEventListener('keydown', onKey);
    modal.addEventListener('click', onClick);
    this.#bound.set(modal.id, { onKey, onClick });
  }

  /** Снять всё — для полной пересборки интерфейса. */
  static destroy() {
    for (const [id, h] of this.#bound) {
      const el = document.getElementById(id);
      if (el) {
        el.removeEventListener('keydown', h.onKey);
        el.removeEventListener('click', h.onClick);
      }
    }
    this.#bound.clear();
    this.#stack.length = 0;
    this.#previousFocus = null;
  }

  /** Состояние — для проверок и панели диагностики. */
  static state() {
    return { open: [...this.#stack], bound: this.#bound.size };
  }
}

export function openModal(id) { ModalManager.open(id); }
export function closeModal(id) { ModalManager.close(id); }
export function closeAllModals() { ModalManager.closeAll(); }

export default ModalManager;
